import { HUNT_BALANCE } from '@kenocyber/game-domain';

/**
 * Procedural neon-dash artwork for projectiles.
 *
 * A shot is a thin elongated laser dash - never a ball, orb or fireball.
 * Circular shapes are reserved for the impact burst.
 *
 * Geometry (logical arena pixels, matching the design brief):
 * - body ~5 px thick, ~34 px long;
 * - bright, near-white leading tip;
 * - short tail that tapers smoothly to nothing.
 *
 * Layers, painted back to front into a single texture:
 * 1. wide translucent neon halo;
 * 2. bright coloured body;
 * 3. near-white core, biased toward the front;
 * 4. a hot tip highlight.
 *
 * The texture bakes the COLOUR in (rather than relying on `setTint`, which
 * would multiply the near-white core down to the body colour and destroy the
 * layering). One texture is generated per distinct colour and cached in
 * Phaser's texture manager, so repeated shots - and every future cannon or
 * skin colour - reuse it instead of allocating anything per shot.
 */

/**
 * On-screen scale of the dash sprite.
 *
 * The texture is authored at full resolution and rendered at half size, which
 * halves the visible length, body, halo, core and tip in one place while
 * keeping the artwork's quality. Redrawing the texture smaller instead would
 * throw away the pixels that make the neon falloff smooth.
 *
 * Purely presentational: speed, damage, cadence, homing and the collision
 * radius are simulation values and are untouched by it.
 */
export const DASH_RENDER_SCALE = 0.5;

/**
 * Extra width on the SHIP's bolts, and on nothing else.
 *
 * Applied to the sprite's LOCAL lateral axis only. The dash texture is authored
 * with its length along x and its thickness along y, and the sprite is rotated
 * to the projectile's heading every frame - so scaling y widens the bolt
 * perpendicular to where it is actually flying, whatever direction homing has
 * turned it. Scaling the screen's horizontal axis instead would stretch a
 * vertical shot lengthwise, which is the opposite of what was asked.
 *
 * `DASH_RENDER_SCALE` stays the length: 1.0 of the authored length, unchanged.
 *
 * The figure is two passes compounded - 1.13, then a further 15% - and it is
 * written as the product rather than as 1.2995 so the history stays legible
 * and a third pass is one more factor rather than a new magic number.
 *
 * Purely presentational. The collision radius, damage, speed, homing, target,
 * cadence, projectile count and trail are simulation or balance values and are
 * untouched - the artwork module cannot reach any of them.
 */
export const DASH_LATERAL_SCALE = 1.13 * 1.15;

/** Trail streaks follow the same reduction so the wake stays in proportion. */
export const STREAK_RENDER_SCALE = 0.5;

/** Total dash length in pixels (design brief: 22-34). */
export const DASH_LENGTH = 34;
/** Vertical room for the halo to fade out without clipping. */
export const DASH_TEXTURE_HEIGHT = 18;
/** Horizontal padding so the tip glow is not cut by the texture edge. */
export const DASH_PAD_X = 3;

/** Body half-thickness at the widest point => ~5 px of solid body. */
export const BODY_HALF = 2.5;
/** Halo half-thickness => ~9 px of glow, wide enough to bloom, tight enough
 *  that the shot never becomes a thick smudge. */
const HALO_HALF = 4.6;
/** Core half-thickness => ~2.2 px of near-white centre. */
const CORE_HALF = 1.1;

const STREAK_TEXTURE_KEY = 'projectile-streak';
const IMPACT_TEXTURE_KEY = 'impact-spark';

/**
 * Minimal drawing surface this module needs.
 *
 * Structural, so `Phaser.Scene` satisfies it without this module importing
 * Phaser - which keeps the artwork maths unit-testable outside a WebGL/canvas
 * runtime.
 */
export interface DashGraphics {
  fillStyle(color: number, alpha?: number): DashGraphics;
  fillRect(x: number, y: number, width: number, height: number): DashGraphics;
  fillCircle(x: number, y: number, radius: number): DashGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

export interface DashTextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: { graphics(config: { x: number; y: number }, addToScene: boolean): DashGraphics };
}

export function rgbChannels(color: number): { r: number; g: number; b: number } {
  return { r: (color >> 16) & 0xff, g: (color >> 8) & 0xff, b: color & 0xff };
}

function mixToWhite(color: number, amount: number): number {
  const { r, g, b } = rgbChannels(color);
  const lift = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return (lift(r) << 16) | (lift(g) << 8) | lift(b);
}

/**
 * Semicircular cap so the dash never ends in a hard flat edge.
 * `t` is 0 at the tail and 1 at the leading tip.
 */
function frontCap(t: number, capStart: number): number {
  if (t <= capStart) {
    return 1;
  }
  const k = (t - capStart) / (1 - capStart);
  return Math.sqrt(Math.max(0, 1 - k * k));
}

/**
 * Thickness profile of a laser bolt: essentially uniform along the body, with
 * the taper confined to the tail.
 *
 * A gradual taper across the whole length would read as a comet or teardrop -
 * exactly the fireball look the design forbids.
 */
function boltProfile(t: number, tailEnd: number, capStart: number): number {
  const tail = t >= tailEnd ? 1 : (Math.max(0, t) / tailEnd) ** 0.8;
  return tail * frontCap(t, capStart);
}

export function dashTextureKey(color: number): string {
  return `projectile-dash-${color.toString(16).padStart(6, '0')}`;
}

/** Which of the three tones a layer is painted with. */
export type DashTone = 'body' | 'core' | 'tip';

export interface DashLayer {
  readonly name: string;
  readonly tone: DashTone;
  /** Half-thickness in pixels at `t` (0 = tail, 1 = leading tip). */
  readonly halfThickness: (t: number) => number;
  readonly alpha: (t: number) => number;
}

/**
 * The dash, described as data so both the painter and the tests read the same
 * definition. Painted in order, back to front.
 *
 * Alphas are deliberately strong: with additive blending on a near-black
 * arena, a timid glow disappears. They stay layered (wide+faint over
 * tight+bright) so the result is neon, not a thick smudge.
 */
export const DASH_LAYERS: readonly DashLayer[] = Object.freeze([
  {
    name: 'outer-halo',
    tone: 'body',
    halfThickness: (t) => HALO_HALF * boltProfile(t, 0.34, 0.88),
    alpha: (t) => 0.1 + 0.3 * t ** 1.2,
  },
  {
    // Tighter and much brighter: this is what makes the shot read as neon
    // rather than a pale line.
    name: 'inner-halo',
    tone: 'body',
    halfThickness: (t) => HALO_HALF * 0.56 * boltProfile(t, 0.3, 0.92),
    alpha: (t) => 0.26 + 0.6 * t ** 0.9,
  },
  {
    // The solid coloured bolt: uniform thickness, tail taper only.
    name: 'body',
    tone: 'body',
    halfThickness: (t) => BODY_HALF * boltProfile(t, 0.26, 0.94),
    alpha: (t) => Math.min(1, 0.5 + 0.6 * t),
  },
  {
    // Near-white core running along most of the bolt.
    name: 'core',
    tone: 'core',
    halfThickness: (t) => CORE_HALF * boltProfile(t, 0.42, 0.95),
    alpha: (t) => (t < 0.22 ? 0 : Math.min(1, 0.45 + ((t - 0.22) / 0.78) ** 0.7)),
  },
  {
    // Hot leading SECTION, faded in gradually so it never detaches into a
    // ball at the front.
    name: 'tip',
    tone: 'tip',
    halfThickness: (t) => (t < 0.78 ? 0 : CORE_HALF * 1.15 * frontCap(t, 0.96)),
    alpha: (t) => (t < 0.78 ? 0 : Math.min(1, ((t - 0.78) / 0.14) ** 0.9)),
  },
]);

/** Tone colours derived from the configured cannon/skin colour. */
export function dashTones(color: number): Readonly<Record<DashTone, number>> {
  return { body: color, core: mixToWhite(color, 0.8), tip: mixToWhite(color, 0.92) };
}

/** Widest half-thickness anywhere on the dash, across every layer. */
export function dashMaxHalfThickness(): number {
  let max = 0;
  for (const layer of DASH_LAYERS) {
    for (let i = 0; i < DASH_LENGTH; i += 1) {
      max = Math.max(max, layer.halfThickness(i / (DASH_LENGTH - 1)));
    }
  }
  return max;
}

/** Total width of the dash texture, in authored pixels. */
export const DASH_TEXTURE_WIDTH = DASH_LENGTH + DASH_PAD_X * 2;

/**
 * A column is DRAWN only if some layer is thick and opaque enough there.
 *
 * The painter skips `half <= 0.05` or `alpha <= 0.005`, so the texture's edges
 * are not its painted edges: three pixels of padding on each side plus the
 * columns where every profile has already tapered to nothing. Anything that
 * reasons about where the artwork actually reaches has to use the same test the
 * painter uses, which is why the condition is stated once, here.
 */
function dashColumnIsPainted(column: number): boolean {
  const t = column / (DASH_LENGTH - 1);
  for (const layer of DASH_LAYERS) {
    if (layer.halfThickness(t) > 0.05 && layer.alpha(t) > 0.005) {
      return true;
    }
  }
  return false;
}

/**
 * Where the artwork really is, measured from the sprite's own pivot.
 *
 * In authored pixels, before any render scale. `forward` is how far the paint
 * reaches AHEAD of {@link DASH_ORIGIN_X} - which is the point the simulation
 * says the projectile is at - and `tail` how far it trails behind it.
 *
 * ## Why this is not "the texture is 40 wide"
 *
 * Because it is not. The origin sits at 34.96 of 40, and the last painted
 * column ends at 36: the bolt reaches barely a pixel past the authoritative
 * point and thirty-one behind it. A renderer that sized a shot by the texture
 * would be drawing five pixels of transparent padding in front of the hitbox
 * and calling it reach - and in a duel, where a shot's collision radius is a
 * number the player is dodging, "how far in front of the point does the light
 * go" is not a detail. `pvp/projectile-art.ts` derives its scale from this.
 */
export function dashPaintedExtent(): {
  readonly forward: number;
  readonly tail: number;
  readonly length: number;
} {
  let first = -1;
  let last = -1;
  for (let column = 0; column < DASH_LENGTH; column += 1) {
    if (!dashColumnIsPainted(column)) {
      continue;
    }
    if (first < 0) {
      first = column;
    }
    last = column;
  }
  if (first < 0) {
    return { forward: 0, tail: 0, length: 0 };
  }
  const origin = DASH_ORIGIN_X * DASH_TEXTURE_WIDTH;
  // `+ 1` because a column is a one-pixel `fillRect` starting at that column.
  const forward = DASH_PAD_X + last + 1 - origin;
  const tail = origin - (DASH_PAD_X + first);
  return { forward, tail, length: forward + tail };
}

/**
 * Creates (once) and returns the dash texture for a colour.
 *
 * Drawn as 1 px columns with a continuous thickness and alpha profile, which
 * keeps the silhouette and the glow smooth - no stepped rectangles and no
 * visible alpha box, which additive blending would otherwise expose.
 */
export function ensureDashTexture(scene: DashTextureScene, color: number): string {
  const key = dashTextureKey(color);
  if (scene.textures.exists(key)) {
    return key;
  }

  const width = DASH_LENGTH + DASH_PAD_X * 2;
  const centreY = DASH_TEXTURE_HEIGHT / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const tones = dashTones(color);

  for (const layer of DASH_LAYERS) {
    const toneColor = tones[layer.tone];
    for (let i = 0; i < DASH_LENGTH; i += 1) {
      const t = i / (DASH_LENGTH - 1);
      const half = layer.halfThickness(t);
      const a = layer.alpha(t);
      if (half <= 0.05 || a <= 0.005) {
        continue;
      }
      graphics.fillStyle(toneColor, Math.min(1, a));
      graphics.fillRect(DASH_PAD_X + i, centreY - half, 1, half * 2);
    }
  }

  graphics.generateTexture(key, width, DASH_TEXTURE_HEIGHT);
  graphics.destroy();
  return key;
}

/**
 * Origin along the dash where the simulation's position sits.
 *
 * Just behind the tip, so the hot leading edge sits slightly ahead of the
 * logical point and the body trails behind it.
 */
export const DASH_ORIGIN_X = (DASH_PAD_X + DASH_LENGTH * 0.94) / (DASH_LENGTH + DASH_PAD_X * 2);

/**
 * Small elongated streak used for the motion trail.
 *
 * Deliberately a lozenge, not a dot: the brief forbids a trail made of little
 * white balls. It is white so it can be tinted with the cannon colour.
 */
export function ensureStreakTexture(scene: DashTextureScene): string {
  if (scene.textures.exists(STREAK_TEXTURE_KEY)) {
    return STREAK_TEXTURE_KEY;
  }
  const length = 16;
  const height = 6;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let i = 0; i < length; i += 1) {
    const t = i / (length - 1);
    // Lozenge: thin at both ends, fullest in the middle.
    const shape = Math.sin(Math.PI * t);
    const half = 1.6 * shape;
    if (half <= 0.05) {
      continue;
    }
    graphics.fillStyle(0xffffff, 0.25 + 0.6 * shape);
    graphics.fillRect(i, height / 2 - half, 1, half * 2);
  }
  graphics.generateTexture(STREAK_TEXTURE_KEY, length, height);
  graphics.destroy();
  return STREAK_TEXTURE_KEY;
}

/**
 * The energy BALL an explosive pet fires.
 *
 * A different weapon deserves a different silhouette: the ship's dash says
 * "fast and thin", and a ball that is about to detonate should say "heavy and
 * full". Still a discrete projectile - one object, one impact, never a beam and
 * never a trail drawn as a solid line.
 *
 * Painted as concentric discs rather than a gradient fill so the same layering
 * the dash uses survives: a near-white core, a saturated body, and a wide faint
 * halo. The colour is baked in for the same reason it is baked into the dash -
 * a multiply tint would flatten the core to the body colour.
 */
export function ensureEnergyBallTexture(scene: DashTextureScene, color: number): string {
  const key = `projectile-ball-${color.toString(16).padStart(6, '0')}`;
  if (scene.textures.exists(key)) {
    return key;
  }
  const size = ENERGY_BALL_TEXTURE_SIZE;
  const centre = size / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const tones = dashTones(color);
  // Back to front: halo, glow, body, core. Drawn as many thin rings so the
  // falloff is smooth instead of four visible steps.
  for (let step = ENERGY_BALL_RINGS; step >= 1; step -= 1) {
    const t = step / ENERGY_BALL_RINGS;
    const radius = centre * t;
    // Alpha rises steeply toward the middle: most of the disc is faint glow
    // and only the heart of it is solid.
    const alpha = 0.05 + (1 - t) ** 2.1 * 0.85;
    const tone = t > 0.55 ? tones.body : t > 0.3 ? tones.core : tones.tip;
    graphics.fillStyle(tone, Math.min(1, alpha));
    graphics.fillCircle(centre, centre, radius);
  }
  graphics.generateTexture(key, size, size);
  graphics.destroy();
  return key;
}

/** Authored size of the ball texture, in pixels. Drawn smaller than this. */
export const ENERGY_BALL_TEXTURE_SIZE = 48;
const ENERGY_BALL_RINGS = 24;

/**
 * On-screen diameter of an energy ball, in arena units.
 *
 * Deliberately generous next to a dash: the ball is the thing that is about to
 * take out half a wave, and it should be legible as it travels.
 */
export const ENERGY_BALL_ARENA_SIZE = 26;

/**
 * A guided MISSILE: a dark metal body with a lit nose and a burning tail.
 *
 * Neither a dash nor a ball, because it is neither. The body is genuinely dark -
 * that is what makes it read as a manufactured object rather than as light -
 * and only the nose cone, the spine, the bands and the exhaust carry the energy
 * colour. Additive blending would wash the body out, so the dark part is drawn
 * opaque and the caller uses a normal blend for it.
 *
 * ## It has to be the biggest thing the pet puts on screen
 *
 * The first version was 30x14 with a 4.8-pixel body, drawn at 0.8 - so the hull
 * came out THINNER than the glow around a ship bolt and the missile simply
 * disappeared into the volley. A weapon that fires one shot a second at triple
 * damage must read as heavier than the stream of bolts around it, so this one
 * is authored at 54x30 with a 13-pixel body and a real flame, and it ships with
 * a separate additive halo (`ensureMissileGlowTexture`) drawn behind it.
 *
 * The measured comparison, on screen, at the render scales below:
 *
 * | Shot        | Length | Solid body | With glow |
 * | ----------- | ------ | ---------- | --------- |
 * | ship bolt   | 17 px  | 3.2 px     | 6.0 px    |
 * | pet missile | 50 px  | 12.0 px    | 36 px     |
 *
 * Purely presentational: speed, damage, cadence, homing, targeting and the
 * collision radius are simulation values and are untouched by any of it.
 *
 * Authored pointing +x, like the dash, so one rotation aims both.
 */
export function ensureMissileTexture(scene: DashTextureScene, color: number): string {
  const key = `projectile-missile-${color.toString(16).padStart(6, '0')}`;
  if (scene.textures.exists(key)) {
    return key;
  }
  const width = MISSILE_LENGTH;
  const height = MISSILE_HEIGHT;
  const centreY = height / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const tones = dashTones(color);

  /*
   * The tail flame occupies the first quarter of the texture.
   *
   * Drawn INSIDE the sprite rather than as a separate emitter: the flame has to
   * turn with the missile, and a burning tail is most of what makes a dark
   * object legible against a dark arena.
   */
  for (let i = 0; i < width; i += 1) {
    const t = i / (width - 1);
    if (t >= MISSILE_HULL_START) {
      continue;
    }
    // Widest at the nozzle, tapering to a point astern.
    const k = t / MISSILE_HULL_START;
    const flame = MISSILE_FLAME_HALF * k ** 0.55;
    for (const [scale, tone, alpha] of [
      [1, tones.body, 0.45],
      [0.62, tones.core, 0.8],
      [0.3, tones.tip, 1],
    ] as const) {
      const half = flame * scale;
      if (half > 0.08) {
        graphics.fillStyle(tone, alpha);
        graphics.fillRect(i, centreY - half, 1, half * 2);
      }
    }
  }

  for (let i = 0; i < width; i += 1) {
    const t = i / (width - 1);
    if (t < MISSILE_HULL_START) {
      continue;
    }
    /*
     * The silhouette: a parallel body and a pointed ogive nose.
     *
     * `half` is the hull's half-thickness at this column - constant along the
     * body, and a quarter-ellipse over the last fifth.
     */
    const along = (t - MISSILE_HULL_START) / (1 - MISSILE_HULL_START);
    const half =
      along > 0.78
        ? MISSILE_BODY_HALF * Math.sqrt(Math.max(0, 1 - ((along - 0.78) / 0.22) ** 2))
        : MISSILE_BODY_HALF;

    if (half > 0.05) {
      /*
       * A dark hull, lit from above.
       *
       * Three greys rather than one: a highlight along the top, a mid tone over
       * the upper half and a shadow below, which is the cheapest thing that
       * makes a cylinder look round in a 2D sprite.
       */
      graphics.fillStyle(0x30353f, 1);
      graphics.fillRect(i, centreY - half, 1, half);
      graphics.fillStyle(0x14161c, 1);
      graphics.fillRect(i, centreY, 1, half);
      graphics.fillStyle(0x707a89, 0.95);
      graphics.fillRect(i, centreY - half, 1, Math.max(1, half * 0.22));
      graphics.fillStyle(0x05060a, 0.85);
      graphics.fillRect(i, centreY + half - Math.max(1, half * 0.18), 1, Math.max(1, half * 0.18));
    }

    /*
     * A thick lit spine down the whole hull.
     *
     * Two layers - a saturated band with a near-white line inside it - which is
     * the same core/body layering the dash uses, at three times the width.
     */
    if (along > 0.06 && along < 0.86) {
      graphics.fillStyle(tones.core, 0.95);
      graphics.fillRect(i, centreY - MISSILE_SPINE_HALF, 1, MISSILE_SPINE_HALF * 2);
      graphics.fillStyle(tones.tip, 1);
      graphics.fillRect(i, centreY - MISSILE_SPINE_HALF * 0.42, 1, MISSILE_SPINE_HALF * 0.84);
    }

    // The nose cone, fully lit: this is the end that arrives.
    if (along > 0.82) {
      graphics.fillStyle(tones.tip, 1);
      graphics.fillRect(i, centreY - half, 1, half * 2);
    }
  }

  /*
   * Two energy bands around the body and a pair of fins at the tail.
   *
   * Drawn after the columns so they sit on top of the hull rather than being
   * overwritten by it.
   */
  const hullFrom = MISSILE_HULL_START * (width - 1);
  const hullSpan = width - 1 - hullFrom;
  for (const along of [0.24, 0.52] as const) {
    graphics.fillStyle(tones.tip, 1);
    graphics.fillRect(
      hullFrom + hullSpan * along,
      centreY - MISSILE_BODY_HALF,
      2,
      MISSILE_BODY_HALF * 2,
    );
  }
  graphics.fillStyle(0x3c424e, 1);
  graphics.fillRect(hullFrom + 1, centreY - MISSILE_BODY_HALF - 4.5, 7, 4.5);
  graphics.fillRect(hullFrom + 1, centreY + MISSILE_BODY_HALF, 7, 4.5);
  graphics.fillStyle(tones.body, 0.9);
  graphics.fillRect(hullFrom + 1, centreY - MISSILE_BODY_HALF - 4.5, 7, 1);
  graphics.fillRect(hullFrom + 1, centreY + MISSILE_BODY_HALF + 3.5, 7, 1);

  graphics.generateTexture(key, width, height);
  graphics.destroy();
  return key;
}

/**
 * The additive HALO a missile carries, as its own sprite.
 *
 * The missile itself is drawn with a normal blend, because an additive dark
 * hull is a transparent one. That leaves it with no bloom at all - so the glow
 * is a second, larger sprite underneath, blended additively, exactly the way
 * the 3D pets put an additive shell around every emissive volume.
 *
 * An ellipse rather than a circle: it is a halo around a long object, and a
 * round one would read as a bubble the missile happens to be inside.
 */
export function ensureMissileGlowTexture(scene: DashTextureScene, color: number): string {
  const key = `projectile-missile-glow-${color.toString(16).padStart(6, '0')}`;
  if (scene.textures.exists(key)) {
    return key;
  }
  const width = MISSILE_LENGTH;
  const height = MISSILE_HEIGHT;
  const centreX = width / 2;
  const centreY = height / 2;
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const tones = dashTones(color);

  // Nested ellipses, outermost first, so the falloff is smooth rather than a
  // few visible steps. Additive blending sums them into one soft bloom.
  for (let band = MISSILE_GLOW_BANDS; band >= 1; band -= 1) {
    const t = band / MISSILE_GLOW_BANDS;
    const alpha = 0.03 + (1 - t) ** 1.8 * 0.42;
    const tone = t > 0.6 ? tones.body : t > 0.3 ? tones.core : tones.tip;
    graphics.fillStyle(tone, alpha);
    for (let i = 0; i < width; i += 1) {
      const dx = (i - centreX) / (halfWidth * t);
      if (Math.abs(dx) >= 1) {
        continue;
      }
      const half = halfHeight * t * Math.sqrt(1 - dx * dx);
      graphics.fillRect(i, centreY - half, 1, half * 2);
    }
  }

  graphics.generateTexture(key, width, height);
  graphics.destroy();
  return key;
}

/** Authored size of the missile sprite, in pixels. */
export const MISSILE_LENGTH = 54;
export const MISSILE_HEIGHT = 30;

/** Where the hull starts along the texture; everything before it is flame. */
const MISSILE_HULL_START = 0.24;
/** Half-thickness of the hull => 13 px of solid body. */
const MISSILE_BODY_HALF = 6.5;
/** Half-thickness of the lit spine running the length of the hull. */
const MISSILE_SPINE_HALF = 2.4;
/** Half-thickness of the flame at the nozzle. */
const MISSILE_FLAME_HALF = 6.2;
/** Ellipse bands in the halo. Enough for a smooth falloff, drawn once. */
const MISSILE_GLOW_BANDS = 14;

/**
 * On-screen scale of a missile.
 *
 * Deliberately near 1: the authored artwork IS the size it should be drawn at.
 * A dash is drawn at half size because its texture carries detail a small bolt
 * does not need; a missile is meant to be the heavy thing on screen.
 */
export const MISSILE_RENDER_SCALE = 0.92;

/**
 * How much larger the halo is than the missile it surrounds.
 *
 * Applied on top of the render scale, about the same origin - so the bloom
 * trails astern rather than being centred on the nose.
 */
export const MISSILE_GLOW_SCALE = 1.3;

/**
 * The missile's pivot, as a fraction of the texture's length.
 *
 * The simulation's point is the projectile's position and a missile arrives
 * nose-first, so the origin sits just behind the tip - the same reasoning as
 * `DASH_ORIGIN_X`, at this texture's proportions.
 */
export const MISSILE_ORIGIN_X = 0.94;

/**
 * The expanding ring an explosion draws.
 *
 * ONE shared white texture, tinted per blast: a texture per energy colour would
 * be a texture per palette per frame of an animated one. A ring rather than a
 * disc, because the point of the visual is to show the RADIUS - a filled circle
 * hides the enemies it is telling you about.
 */
export function ensureShockwaveTexture(scene: DashTextureScene): string {
  if (scene.textures.exists(SHOCKWAVE_TEXTURE_KEY)) {
    return SHOCKWAVE_TEXTURE_KEY;
  }
  const size = 128;
  const centre = size / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  /*
   * Painted from the outside in, as filled circles.
   *
   * `Graphics` has no ring primitive that anti-aliases well, so the ring is a
   * bright annulus built by drawing a wide faint disc, then progressively
   * smaller ones, and finally punching the middle back out with the scene's
   * own transparency. Additive blending in the caller does the rest.
   */
  for (let step = 40; step >= 0; step -= 1) {
    const t = step / 40;
    const radius = centre * (0.55 + t * 0.45);
    // A band: brightest at the wave front, fading both inward and outward.
    const band = Math.exp(-((t - 0.62) ** 2) / 0.02);
    if (band < 0.02) {
      continue;
    }
    graphics.fillStyle(0xffffff, Math.min(1, band * 0.9));
    graphics.fillCircle(centre, centre, radius);
  }
  graphics.generateTexture(SHOCKWAVE_TEXTURE_KEY, size, size);
  graphics.destroy();
  return SHOCKWAVE_TEXTURE_KEY;
}

const SHOCKWAVE_TEXTURE_KEY = 'explosion-shockwave';

/** Round spark - impacts only. Never used for a projectile in flight. */
export function ensureImpactTexture(scene: DashTextureScene): string {
  if (scene.textures.exists(IMPACT_TEXTURE_KEY)) {
    return IMPACT_TEXTURE_KEY;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(0xffffff, 0.5).fillCircle(4, 4, 4);
  graphics.fillStyle(0xffffff, 1).fillCircle(4, 4, 2);
  graphics.generateTexture(IMPACT_TEXTURE_KEY, 8, 8);
  graphics.destroy();
  return IMPACT_TEXTURE_KEY;
}

export { IMPACT_TEXTURE_KEY, SHOCKWAVE_TEXTURE_KEY, STREAK_TEXTURE_KEY };

/* -------------------------------------------------------------------------- */
/*  The beam                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The CONTINUOUS LASER, in layers - and the only one in the game.
 *
 * `docs/PETS_V1_SPEC.md` authorises a beam for the Void Butterfly and for
 * nothing else, ever. No ship shot, no cannon, and no other pet may become one.
 * Everything else in this module draws a discrete projectile and must keep
 * doing so.
 *
 * ## Why these textures are WHITE
 *
 * The dash bakes its colour in, because a multiply tint would drag its
 * near-white core down to the body colour and destroy the layering. A beam has
 * no such problem: its layering is three SEPARATE sprites of different
 * thicknesses, so each one can be a flat white strip that the renderer tints -
 * and that matters, because a beam wearing an animated palette would otherwise
 * demand a fresh texture every frame and never stop generating them.
 *
 * Authored pointing +x with the origin at the left edge, so one sprite stretched
 * to the distance and rotated to the angle spans emitter to target exactly.
 */
export function ensureBeamTexture(scene: DashTextureScene, layer: BeamLayer): string {
  const key = `beam-${layer}`;
  if (scene.textures.exists(key)) {
    return key;
  }
  const height = BEAM_TEXTURE_HEIGHT;
  const centreY = height / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const half = BEAM_HALF_THICKNESS[layer];
  /*
   * A soft-edged strip rather than a hard rectangle.
   *
   * Painted as rows whose alpha falls off from the middle, so the beam has a
   * gradient across its width - which is what stops three stacked rectangles
   * reading as a flat ribbon of tape.
   */
  for (let y = 0; y < height; y += 1) {
    const distance = Math.abs(y + 0.5 - centreY) / half;
    if (distance >= 1) {
      continue;
    }
    const alpha = (1 - distance ** 2) ** BEAM_FALLOFF[layer];
    graphics.fillStyle(0xffffff, Math.min(1, alpha));
    graphics.fillRect(0, y, BEAM_TEXTURE_LENGTH, 1);
  }
  graphics.generateTexture(key, BEAM_TEXTURE_LENGTH, height);
  graphics.destroy();
  return key;
}

/** The three concentric layers a beam is drawn from, widest first. */
export type BeamLayer = 'halo' | 'inner' | 'core';
export const BEAM_LAYERS: readonly BeamLayer[] = Object.freeze(['halo', 'inner', 'core']);

/**
 * Authored strip size. Length is arbitrary - the sprite is stretched to the
 * distance - but it must be long enough that the horizontal filter has pixels
 * to work with.
 */
const BEAM_TEXTURE_LENGTH = 16;
export const BEAM_TEXTURE_HEIGHT = 32;

/**
 * Half-thickness of each layer in the authored texture.
 *
 * The ratio between them IS the neon look: a very thin near-white core inside a
 * saturated body inside a wide faint halo. Flattening it turns the beam into
 * the "wall of light" the brief forbids.
 */
const BEAM_HALF_THICKNESS: Readonly<Record<BeamLayer, number>> = Object.freeze({
  halo: 15,
  inner: 6.5,
  core: 2.2,
});

/** How sharply each layer fades across its width. Higher is tighter. */
const BEAM_FALLOFF: Readonly<Record<BeamLayer, number>> = Object.freeze({
  halo: 2.4,
  inner: 1.4,
  core: 0.6,
});

/**
 * On-screen thickness of each layer, in arena units, at EV. I.
 *
 * The first pass sized the core at 3.4 - about a ship bolt's bright centre -
 * reasoning that a laser should read as PRECISE. In the arena that was a
 * hairline: the pet's own wings are 150 pixels across and the beam crossing
 * half the screen was thinner than the bolts flying past it. It is a weapon,
 * and it now looks like one, while still being a line rather than the wall of
 * light the brief forbids.
 */
export const BEAM_ARENA_THICKNESS: Readonly<Record<BeamLayer, number>> = Object.freeze({
  halo: 34,
  inner: 15,
  core: 5.5,
});

/**
 * How much thicker the beam is at each evolution.
 *
 * EV. III applies ten damage instances a second against EV. I's six, and a
 * weapon that hits nearly twice as hard should not draw the same line. Applied
 * to every part of the effect - strips, packets and the impact bloom - so the
 * whole thing grows together rather than a fat core inside a thin halo.
 */
export const BEAM_EVOLUTION_GAUGE: readonly number[] = Object.freeze([1, 1.22, 1.45]);

/** Alpha of each layer, before the firing pulse. */
export const BEAM_LAYER_ALPHA: Readonly<Record<BeamLayer, number>> = Object.freeze({
  halo: 0.3,
  inner: 0.62,
  core: 0.95,
});

/**
 * A travelling PULSE and the impact bloom, sharing one round white texture.
 *
 * Round, small and tinted - the same shape works for a packet of energy running
 * up the beam and for the glow where it lands, at different scales.
 */
export function ensureBeamPulseTexture(scene: DashTextureScene): string {
  if (scene.textures.exists(BEAM_PULSE_TEXTURE_KEY)) {
    return BEAM_PULSE_TEXTURE_KEY;
  }
  const size = 32;
  const centre = size / 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  for (let step = 16; step >= 1; step -= 1) {
    const t = step / 16;
    graphics.fillStyle(0xffffff, 0.04 + (1 - t) ** 2 * 0.9);
    graphics.fillCircle(centre, centre, centre * t);
  }
  graphics.generateTexture(BEAM_PULSE_TEXTURE_KEY, size, size);
  graphics.destroy();
  return BEAM_PULSE_TEXTURE_KEY;
}

const BEAM_PULSE_TEXTURE_KEY = 'beam-pulse';

/** How many energy packets travel the beam at once. A fixed, pooled set. */
export const BEAM_PULSE_COUNT = 4;
/** How long a packet takes to travel emitter to target, milliseconds. */
export const BEAM_PULSE_TRAVEL_MS = 520;

export { BEAM_PULSE_TEXTURE_KEY };

/**
 * How thick a bolt is drawn, as a fraction of the hull that fired it.
 *
 * The one number that makes "the same size shot" mean something across two
 * modes whose worlds are different sizes. A hunt hull is 96 units and a duel
 * hull is 52, so comparing world units - or worse, screenshot pixels from two
 * cameras - compares nothing. The ratio is what a player actually reads.
 *
 * Derived, never chosen: it is what the hunt already draws, read back out of
 * the same profile and the same scales the hunt renders with. 5.98 units of
 * paint on a 96-unit hull, which is 0.0623.
 *
 * It is presentation. Nothing that decides a hit reads it - the hunt's own
 * `hitRadius` is 10, a 20-unit circle, and this draws under a third of it,
 * which has always been this game's grammar.
 */
export const HUNT_BOLT_THICKNESS_PER_HULL =
  (2 * dashMaxHalfThickness() * DASH_RENDER_SCALE * DASH_LATERAL_SCALE) /
  HUNT_BALANCE.ship.displayWidth;
